import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  DeviceType,
  TourPath,
  SkipReason,
  TourWithProgress,
  ToursListResponse,
  TourActionResponse,
} from '../types';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all tours with user's progress on both platforms.
 */
async function getAllTours(): Promise<ToursListResponse> {
  return apiFetch<ToursListResponse>('/tours');
}

/**
 * Fetch a specific tour with user's progress.
 */
async function getTour(tourId: string): Promise<TourWithProgress> {
  return apiFetch<TourWithProgress>(`/tours/${tourId}`);
}

/**
 * Start a tour on a specific device type.
 */
async function startTour(
  tourId: string,
  deviceType: DeviceType,
  selectedPath?: TourPath
): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/start`, {
    method: 'POST',
    body: JSON.stringify({ deviceType, selectedPath }),
  });
}

/**
 * Mark a tour step as completed.
 */
async function completeStep(
  tourId: string,
  deviceType: DeviceType,
  stepId: string,
  selectedPath?: TourPath
): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/complete-step`, {
    method: 'POST',
    body: JSON.stringify({ deviceType, stepId, selectedPath }),
  });
}

/**
 * Mark a tour as completed.
 */
async function completeTour(tourId: string, deviceType: DeviceType): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ deviceType }),
  });
}

/**
 * Skip a tour permanently.
 */
async function skipTour(
  tourId: string,
  deviceType: DeviceType,
  reason?: SkipReason
): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/skip`, {
    method: 'POST',
    body: JSON.stringify({ deviceType, reason }),
  });
}

/**
 * Dismiss tour temporarily (remind me later).
 */
async function remindLater(tourId: string, deviceType: DeviceType): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/remind-later`, {
    method: 'POST',
    body: JSON.stringify({ deviceType }),
  });
}

/**
 * Exit tour mid-progress, saving current state.
 */
async function exitTour(tourId: string, deviceType: DeviceType): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/exit`, {
    method: 'POST',
    body: JSON.stringify({ deviceType }),
  });
}

/**
 * Resume a tour from saved progress.
 */
async function resumeTour(
  tourId: string,
  deviceType: DeviceType,
  selectedPath?: TourPath
): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/resume`, {
    method: 'POST',
    body: JSON.stringify({ deviceType, selectedPath }),
  });
}

/**
 * Reset a tour to not_started status.
 */
async function resetTour(tourId: string, deviceType: DeviceType): Promise<TourActionResponse> {
  return apiFetch<TourActionResponse>(`/tours/${tourId}/reset`, {
    method: 'POST',
    body: JSON.stringify({ deviceType }),
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch all tours with user's progress.
 * Returns tour definitions with progress for both web and mobile platforms.
 */
export function useTours(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.tours.list(),
    queryFn: getAllTours,
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch a specific tour with user's progress.
 */
export function useTour(tourId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.tours.detail(tourId),
    queryFn: () => getTour(tourId),
    enabled: options?.enabled ?? !!tourId,
    staleTime: 60 * 1000, // 1 minute
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to start a tour.
 * Invalidates tour queries on success.
 */
export function useStartTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tourId,
      deviceType,
      selectedPath,
    }: {
      tourId: string;
      deviceType: DeviceType;
      selectedPath?: TourPath;
    }) => startTour(tourId, deviceType, selectedPath),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to complete a tour step.
 * Invalidates tour queries on success.
 */
export function useCompleteStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tourId,
      deviceType,
      stepId,
      selectedPath,
    }: {
      tourId: string;
      deviceType: DeviceType;
      stepId: string;
      selectedPath?: TourPath;
    }) => completeStep(tourId, deviceType, stepId, selectedPath),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to complete a tour.
 * Invalidates tour queries on success.
 */
export function useCompleteTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tourId, deviceType }: { tourId: string; deviceType: DeviceType }) =>
      completeTour(tourId, deviceType),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to skip a tour permanently.
 * Invalidates tour queries on success.
 */
export function useSkipTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tourId,
      deviceType,
      reason,
    }: {
      tourId: string;
      deviceType: DeviceType;
      reason?: SkipReason;
    }) => skipTour(tourId, deviceType, reason),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to dismiss a tour temporarily (remind me later).
 * Invalidates tour queries on success.
 */
export function useRemindLater() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tourId, deviceType }: { tourId: string; deviceType: DeviceType }) =>
      remindLater(tourId, deviceType),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to exit a tour mid-progress.
 * Saves current progress for later resumption.
 */
export function useExitTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tourId, deviceType }: { tourId: string; deviceType: DeviceType }) =>
      exitTour(tourId, deviceType),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to resume a tour from saved progress.
 */
export function useResumeTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tourId,
      deviceType,
      selectedPath,
    }: {
      tourId: string;
      deviceType: DeviceType;
      selectedPath?: TourPath;
    }) => resumeTour(tourId, deviceType, selectedPath),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

/**
 * Hook to reset a tour to not_started status.
 * The tour can then be prompted again.
 */
export function useResetTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tourId, deviceType }: { tourId: string; deviceType: DeviceType }) =>
      resetTour(tourId, deviceType),
    onSuccess: (_, { tourId }) => {
      // Invalidate the specific tour
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.detail(tourId) });
      // Invalidate tours list
      queryClient.invalidateQueries({ queryKey: queryKeys.tours.list() });
    },
  });
}

// ============================================================================
// Helper Hooks
// ============================================================================

/**
 * Helper type for tour progress on a specific platform.
 */
export type PlatformProgress = TourWithProgress['web'];

/**
 * Determines if a tour should be prompted to the user.
 * Returns true if the tour hasn't been completed or skipped on this platform.
 */
export function shouldPromptTour(
  progress: PlatformProgress | undefined,
  options?: {
    /** Ignore reminded status and show anyway */
    ignoreReminded?: boolean;
    /** Time in hours before re-prompting after remind */
    remindCooldownHours?: number;
  }
): boolean {
  if (!progress) return true;

  const { ignoreReminded = false, remindCooldownHours = 24 } = options ?? {};

  // Never prompt if completed or skipped
  if (progress.status === 'completed' || progress.status === 'skipped') {
    return false;
  }

  // If reminded, check cooldown
  if (progress.status === 'reminded' && !ignoreReminded && progress.remindedAt) {
    const remindedAt = new Date(progress.remindedAt);
    const cooldownMs = remindCooldownHours * 60 * 60 * 1000;
    const now = new Date();
    if (now.getTime() - remindedAt.getTime() < cooldownMs) {
      return false;
    }
  }

  return true;
}

/**
 * Determines if a tour should be actively shown (already started).
 */
export function shouldShowTour(progress: PlatformProgress | undefined): boolean {
  if (!progress) return false;
  return progress.status === 'in_progress';
}

/**
 * Checks if the user has completed a tour on another platform.
 * Useful for showing "Completed on [platform]" messages.
 */
export function hasCompletedOnOtherPlatform(
  tour: TourWithProgress | undefined,
  currentPlatform: DeviceType
): boolean {
  if (!tour) return false;

  const otherPlatform = currentPlatform === 'web' ? 'mobile' : 'web';
  return tour[otherPlatform].status === 'completed';
}
